import React, { useEffect, useState, useMemo } from "react";
import { Box, Button, Typography, CircularProgress, Grid, TextField, Autocomplete, Chip, InputAdornment, useTheme } from "@mui/material";
import EmailIcon from "@mui/icons-material/EmailOutlined";
import PhoneIcon from "@mui/icons-material/PhoneIphoneOutlined";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import PersonIcon from "@mui/icons-material/Person";
import DescriptionIcon from "@mui/icons-material/Description";

import { ErrorSnackbar } from "../components/Snackbar/ErrorSnackbar";
import { SuccessSnackbar } from "../components/Snackbar/SuccessSnackbar";
import { parseBackendErrorMessage } from "../utils/parseBackendError";
import { isValidEmail, isValidPhone } from "../utils/userValidators";
import { useNavigate } from "react-router-dom";
import BulkLoadResultsModal from "../components/Modal/BulkLoadResultsModal";
import WarningModal from "../components/Modal/WarningModal";
import { exportErrorsToExcel } from "../utils/exportErrorsToExcel";
import { useStore } from "../store/useStore";

import useCreateUser from "../hooks/useCreateUser";
import useBulkCreateUsers from "../hooks/useBulkCreateUsers";
import useFetchUserRoles from "../hooks/useFetchUserRoles";

import AssignmentPicker from "../components/AssignmentPicker";
import BulkLoadModal from "../components/Modal/BulkLoadModal";

const toEpoch = (dateString) => (dateString ? Math.floor(new Date(dateString).getTime() / 1000) : null);

// Esquema para exportar errores en la carga de usuarios desde Excel
const USERS_ERRORS_SCHEMA = [
  { header: "Fila_archivo_carga", width: 8, key: "row" },
  { header: "Email", width: 30, key: "email" },
  {
    header: "Mensajes_errores",
    width: 60,
    value: (_item, errs, parse) => errs.map((e) => parse(e?.message ?? e)).join(",\n"),
  },
  {
    header: "Códigos_errores",
    width: 25,
    value: (_item, errs) =>
      errs
        .map((e) => e?.code)
        .filter(Boolean)
        .join(",\n"),
  },
];

const UserCreatePage = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const { data: rolesData, loading: loadingRoles, error: rolesError } = useFetchUserRoles();
  const { showUserCreatedInfoModal, setShowUserCreatedInfoModal } = useStore();

  const { createUser, loading: creating, error: createError } = useCreateUser();
  const { bulkCreateUsers } = useBulkCreateUsers();

  const [excelLoading, setExcelLoading] = useState(false);
  const [excelResult, setExcelResult] = useState(null);
  const [showExcelModal, setShowExcelModal] = useState(false);

  const [openImportModal, setOpenImportModal] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showCreatedModal, setShowCreatedModal] = useState(false);

  const roles = useMemo(() => rolesData?.roles ?? [], [rolesData]);

  const [formData, setFormData] = useState({
    name: "",
    lastName: "",
    email: "",
    phone: "",
    idNumber: "",
    roles: ["participant"],
  });

  const [assign, setAssign] = useState({
    appId: "",
    activationStart: "",
    activationEnd: "",
    selectedQuestionnaireItems: [],
  });

  const TEMPLATE_URL = `${process.env.PUBLIC_URL || ""}/plantillas/crear_usuarios_plantilla.xlsx`;

  useEffect(() => {
    const err = createError || rolesError;
    if (err) {
      const translated = parseBackendErrorMessage(err.message || String(err));
      setErrorMessage(translated);
      setShowError(true);
    }
  }, [createError, rolesError]);

  useEffect(() => {
    const hasParticipant = roles.some((r) => r.id === "participant");
    if (hasParticipant) {
      setFormData((prev) => ({ ...prev, roles: ["participant"] }));
    } else if (roles.length > 0 && !prevHasAnyRole(formData.roles, roles)) {
      setFormData((prev) => ({ ...prev, roles: [roles[0].id] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles.length]);

  const prevHasAnyRole = (current, options) => current?.some((id) => options.some((r) => r.id === id));

  const handleChange = (field) => (e) => setFormData((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowError(false);
    setShowSuccess(false);

    const { name, lastName, email, phone, idNumber, roles: selectedRoles } = formData;

    // Validaciones básicas
    if (!name.trim() || !lastName.trim() || !email.trim() || !phone.trim() || !idNumber.trim()) {
      setErrorMessage("Por favor, complete todos los campos de información básica.");
      setShowError(true);
      return;
    }
    if (!isValidEmail(email)) {
      setErrorMessage("El correo electrónico no tiene un formato válido.");
      setShowError(true);
      return;
    }
    if (!isValidPhone(phone)) {
      setErrorMessage("El teléfono debe tener 10 dígitos.");
      setShowError(true);
      return;
    }

    // Validación de asignación
    if (!assign.appId) {
      setErrorMessage("Seleccione una aplicación para la asignación.");
      setShowError(true);
      return;
    }
    if (!assign.activationStart || !assign.activationEnd) {
      setErrorMessage("Debe definir la ventana de tiempo de la asignación.");
      setShowError(true);
      return;
    }
    const startTs = new Date(assign.activationStart).getTime();
    const endTs = new Date(assign.activationEnd).getTime();
    if (isNaN(startTs) || isNaN(endTs)) {
      setErrorMessage("Las fechas ingresadas no son válidas.");
      setShowError(true);
      return;
    }
    if (endTs <= startTs) {
      setErrorMessage("La fecha de fin de la ventana no puede ser igual o inferior a la de inicio.");
      setShowError(true);
      return;
    }
    if (!assign.selectedQuestionnaireItems?.length) {
      setErrorMessage("Debe agregar al menos un cuestionario a la asignación.");
      setShowError(true);
      return;
    }

    const payload = {
      user: {
        name,
        last_name: lastName,
        email,
        phone_number: `+57${phone}`,
        id_document: idNumber,
        roles: selectedRoles,
      },
      assignapp: {
        app_id: Number(assign.appId),
        questionnaires: assign.selectedQuestionnaireItems.map((it) => ({
          questionnaire_id: it.questionnaireId,
          context: it.context || null,
        })),
        activation_window_start: toEpoch(assign.activationStart),
        activation_window_end: toEpoch(assign.activationEnd),
      },
    };

    const success = await createUser(payload);

    if (!success) {
      setErrorMessage("No se pudo crear el usuario. Por favor revisa los datos o intenta nuevamente.");
      setShowError(true);
      return;
    }

    setShowSuccess(true);
    if (!showUserCreatedInfoModal) setShowCreatedModal(true);

    setFormData({
      name: "",
      lastName: "",
      email: "",
      phone: "",
      idNumber: "",
      roles: ["participant"],
    });
    setAssign({
      appId: "",
      activationStart: "",
      activationEnd: "",
      selectedQuestionnaireItems: [],
    });
  };

  const handleImportUsers = async (file) => {
    setExcelLoading(true);
    const result = await bulkCreateUsers(file);
    setExcelLoading(false);

    // Archivo vacío
    if (result?.error?.message?.includes("undefined method `upto'")) {
      setErrorMessage("El archivo está vacío o no contiene filas válidas.");
      setShowError(true);
      return false;
    }
    // Encabezado incorrecto u otros errores de estructura
    if (result?.data?.errors) {
      const encabezadoError = result.data.errors.find((e) => e.includes("Encabezado incorrecto"));
      if (encabezadoError) {
        setErrorMessage("El archivo no contiene la estructura correcta.");
        setShowError(true);
        return false;
      }
    }

    // Éxito
    if (result?.data && !result?.data?.errors) {
      setExcelResult(result.data);
      setShowExcelModal(true);
      if (result.data.created > 0 && !showUserCreatedInfoModal) {
        setShowCreatedModal(true);
      }
      return true;
    }

    if (result?.error) {
      setErrorMessage("Hubo un error al procesar el archivo.");
      setShowError(true);
    }
    return false;
  };

  return (
    <Box
      maxWidth="700px"
      mx="auto"
      p={{ xs: 2, sm: 4 }}
      bgcolor="white"
      borderRadius={3}
      boxShadow={theme.shadows[3]}
      sx={{
        border: `1px solid ${theme.palette.grey[200]}`,
        transition: "box-shadow 0.3s ease",
        "&:hover": { boxShadow: theme.shadows[6] },
      }}
    >
      <Typography variant="h5" fontWeight={600} mb={4} color="#333333" sx={{ gap: 1.5 }}>
        Crear nuevo usuario
      </Typography>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Sección de Información Básica */}
          <Grid item xs={12}>
            <Typography
              variant="subtitle2"
              fontWeight={500}
              mb={2}
              color="text.primary"
              sx={{ paddingBottom: 1, borderBottom: `2px solid ${theme.palette.tertiary.main}`, display: "flex", alignItems: "center", gap: 1 }}
            >
              Información básica
            </Typography>
          </Grid>

          {/* Nombres */}
          <Grid item xs={12} md={6}>
            <TextField
              inputProps={{ maxLength: 100 }}
              size="small"
              label="Nombres"
              fullWidth
              variant="outlined"
              value={formData.name}
              onChange={handleChange("name")}
              type="text"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="tertiary" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Apellidos */}
          <Grid item xs={12} md={6}>
            <TextField
              inputProps={{ maxLength: 100 }}
              size="small"
              label="Apellidos"
              fullWidth
              variant="outlined"
              value={formData.lastName}
              onChange={handleChange("lastName")}
              type="text"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="tertiary" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Email */}
          <Grid item xs={12} md={6}>
            <TextField
              inputProps={{ maxLength: 128 }}
              size="small"
              label="Correo electrónico"
              fullWidth
              variant="outlined"
              value={formData.email}
              onChange={handleChange("email")}
              type="email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="tertiary" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Teléfono */}
          <Grid item xs={12} md={6}>
            <TextField
              size="small"
              label="Teléfono"
              fullWidth
              value={formData.phone}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                setFormData((prev) => ({ ...prev, phone: value }));
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <PhoneIcon color="tertiary" />
                      <Typography variant="body2" color="text.secondary">
                        +57
                      </Typography>
                    </Box>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Identificación */}
          <Grid item xs={12} md={6}>
            <TextField
              inputProps={{ maxLength: 15 }}
              size="small"
              label="Número de identificación"
              fullWidth
              value={formData.idNumber}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                setFormData((prev) => ({ ...prev, idNumber: value }));
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AssignmentIndIcon color="tertiary" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Roles */}
          <Grid item xs={12} md={6}>
            <Autocomplete
              disabled
              size="small"
              multiple
              options={roles}
              loading={loadingRoles}
              getOptionLabel={(option) => option.name}
              value={roles.filter((role) => formData.roles.includes(role.id))}
              onChange={(_, newValue) =>
                setFormData((prev) => ({
                  ...prev,
                  roles: newValue.map((r) => r.id),
                }))
              }
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return <Chip key={option.id} label={option.name} color="tertiary" variant="outlined" {...tagProps} />;
                })
              }
              renderInput={(params) => <TextField size="small" {...params} label="Seleccionar roles" placeholder="Buscar roles..." />}
            />
          </Grid>

          {/* Asignaciones */}
          <Grid item xs={12}>
            <Typography
              variant="subtitle2"
              fontWeight={500}
              mb={2}
              color="text.primary"
              sx={{ paddingBottom: 1, borderBottom: `2px solid ${theme.palette.tertiary.main}` }}
            >
              Asignaciones
            </Typography>
          </Grid>
          {/* Sección de asignación */}
          <Grid item xs={12}>
            <AssignmentPicker
              value={assign}
              onChange={setAssign}
              busy={creating || excelLoading}
              errorHook={(msg) => {
                setErrorMessage(msg);
                setShowError(true);
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={4}
              sx={{ paddingTop: 3, width: "100%", gap: 2, flexWrap: "wrap" }}
            >
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  localStorage.setItem("forceRefreshUsers", Date.now().toString());
                  navigate("/usuarios");
                }}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                Volver a usuarios
              </Button>

              <Button
                variant="outlined"
                size="large"
                disabled={excelLoading}
                sx={{ textTransform: "none", borderRadius: 2 }}
                onClick={() => setOpenImportModal(true)}
              >
                {excelLoading ? "Cargando..." : "Cargar usuarios por Excel"}
              </Button>

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={creating || loadingRoles}
                sx={{ textTransform: "none", borderRadius: 2 }}
                startIcon={creating && <CircularProgress size={24} color="inherit" />}
              >
                {creating ? "Creando usuario..." : "Crear usuario"}
              </Button>
            </Box>
          </Grid>

          {excelLoading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 4, pl: 4, mt: 4 }}>
              <CircularProgress size={24} sx={{ color: theme.palette.primary.main }} />
              <Typography variant="body1" color="text.primary">
                Procesando usuarios desde el archivo. Este proceso puede tardar algunos minutos...
              </Typography>
            </Box>
          )}
        </Grid>
      </form>

      {/* Snackbars y modales */}
      <ErrorSnackbar open={showError} onClose={() => setShowError(false)} title="Error al crear usuario" message={errorMessage} />

      <SuccessSnackbar
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Usuario creado"
        message="El usuario fue creado correctamente."
      />

      {/* Modal de resultados de importación */}
      <BulkLoadResultsModal
        open={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        result={excelResult}
        title="Resultado de carga"
        labels={{
          total: "Total usuarios",
          created: "Creados",
          updated: "Actualizados",
          failed: "Errores",
          skipped: "Sin cambios",
        }}
        keys={{
          total: "total",
          created: "created",
          updated: "updated",
          failed: "failed",
          skipped: "skipped",
          errorDetails: "error_details",
        }}
        parseErrorMessage={parseBackendErrorMessage}
        onDownloadErrors={(details) =>
          exportErrorsToExcel(details, {
            filename: "errores_usuarios_lote.xlsx",
            sheetName: "Errores",
            schema: USERS_ERRORS_SCHEMA,
          })
        }
      />

      <WarningModal
        open={showCreatedModal}
        onClose={() => {
          setShowCreatedModal(false);
          setShowUserCreatedInfoModal(true);
        }}
        onConfirm={() => {
          setShowCreatedModal(false);
          setShowUserCreatedInfoModal(true);
        }}
        title="¡Importante!"
        description={
          <>
            Para ver a los nuevos usuarios en la lista, por favor vuelva usando el botón{" "}
            <Typography component="span" sx={{ fontWeight: "bold", display: "inline" }}>
              Volver a usuarios
            </Typography>
            . Si usa el botón de retroceso del navegador podría ver la información desactualizada.
          </>
        }
      />

      {/* Modal de importación por Excel*/}
      <BulkLoadModal
        open={openImportModal}
        onClose={() => setOpenImportModal(false)}
        onSelectFile={handleImportUsers}
        title="Cargar usuarios por Excel"
        description="Puede descargar la plantilla requerida para la creación en lote de usuarios y cargarla desde su equipo."
        template={{ url: TEMPLATE_URL, label: "Descargar plantilla" }}
        accept=".xlsx"
        maxSizeMB={10}
        uploadButtonLabel="Subir y crear"
        icon={<DescriptionIcon fontSize="large" color="tertiary" />}
        warnings={[
          "El teléfono, identificación y roles son opcionales, pero si se incluyen deben seguir el formato correcto.",
          "Teléfonos en formato: +57XXXXXXXXXX",
        ]}
        closeOnSuccess={true}
      />
    </Box>
  );
};

export default UserCreatePage;
